// ============================================================================
// Frame Profiler - Per-layer render timing and stutter detection
// ============================================================================
// Zero-allocation profiler for diagnosing client-side microstutters.
// Uses preallocated Float64Array rolling buffers (same pattern as server DebugStats).
// Only active when debug flag showFrameProfile is enabled.
//
// Usage:
//   - Call recordSectionTime() from renderer and game loop to time named sections
//   - Call recordNetworkMessage() from network handler to accumulate per-frame cost
//   - Call recordFrame() at end of each frame for stutter detection + percentiles
//   - Call getProfileSnapshot() from debug overlay (sparingly, ~2x/sec) for UI data
// ============================================================================

import { signal } from '@preact/signals';

// ============================================================================
// Constants
// ============================================================================

// Rolling buffer capacity - 1 second at 60fps
const SECTION_CAPACITY = 60;
// Frame history for percentile calculation - 5 seconds at 60fps
const HISTORY_CAPACITY = 300;
// Stutter thresholds (milliseconds)
const STUTTER_MS = 20;
const SEVERE_STUTTER_MS = 50;
// GC pause heuristic: frame time > N times rolling average
const GC_MULTIPLIER = 3;

// ============================================================================
// Rolling Buffer (preallocated, zero GC pressure)
// ============================================================================

interface RollingBuffer {
  readonly values: Float64Array;
  readonly capacity: number;
  writeIndex: number;
  count: number;
}

const createBuffer = ({ capacity }: { capacity: number }): RollingBuffer => ({
  values: new Float64Array(capacity),
  capacity,
  writeIndex: 0,
  count: 0,
});

const pushToBuffer = ({ buffer, value }: { buffer: RollingBuffer; value: number }): void => {
  buffer.values[buffer.writeIndex] = value;
  buffer.writeIndex = (buffer.writeIndex + 1) % buffer.capacity;
  if (buffer.count < buffer.capacity) {
    buffer.count++;
  }
};

const bufferAvg = ({ buffer }: { buffer: RollingBuffer }): number => {
  if (buffer.count === 0) {
    return 0;
  }
  let sum = 0;
  for (let i = 0; i < buffer.count; i++) {
    sum += buffer.values[i] ?? 0;
  }
  return sum / buffer.count;
};

const bufferMax = ({ buffer }: { buffer: RollingBuffer }): number => {
  if (buffer.count === 0) {
    return 0;
  }
  let max = 0;
  for (let i = 0; i < buffer.count; i++) {
    const v = buffer.values[i] ?? 0;
    if (v > max) {
      max = v;
    }
  }
  return max;
};

const bufferLast = ({ buffer }: { buffer: RollingBuffer }): number => {
  if (buffer.count === 0) {
    return 0;
  }
  const idx = (buffer.writeIndex - 1 + buffer.capacity) % buffer.capacity;
  return buffer.values[idx] ?? 0;
};

const resetBuffer = ({ buffer }: { buffer: RollingBuffer }): void => {
  buffer.writeIndex = 0;
  buffer.count = 0;
};

// ============================================================================
// Section Definitions
// ============================================================================

// Render layer names (order matches Renderer.ts draw calls)
const RENDER_LAYER_NAMES = [
  'tiles',
  'water',
  'territory',
  'objects',
  'construction',
  'items',
  'corpses',
  'debugOverlays',
  'paths',
  'npcs',
  'pawns',
  'projectiles',
  'selection',
  'fog',
  'environment',
  'selectionBox',
  'debugHUD',
] as const;

// Game loop phase names
const PHASE_NAMES = ['update', 'render'] as const;

// Network section
const NETWORK_SECTION = 'network' as const;

// All section names combined
const ALL_SECTIONS = [...RENDER_LAYER_NAMES, ...PHASE_NAMES, NETWORK_SECTION] as const;

// ============================================================================
// Module State
// ============================================================================

let _enabled = false;

// Per-section rolling buffers (preallocated for all known sections)
const _sectionBuffers = new Map<string, RollingBuffer>();
for (const name of ALL_SECTIONS) {
  _sectionBuffers.set(name, createBuffer({ capacity: SECTION_CAPACITY }));
}

// Frame time history for percentile calculation
const _frameHistory = createBuffer({ capacity: HISTORY_CAPACITY });

// PERFORMANCE: Reused sort buffer for percentile calculation (no allocation)
const _sortBuf = new Float64Array(HISTORY_CAPACITY);

// Stutter detection - rolling window over HISTORY_CAPACITY frames
const _stutterFlags = new Uint8Array(HISTORY_CAPACITY); // 0=ok, 1=stutter, 2=severe
let _stutterWriteIdx = 0;
let _stutterHistoryLen = 0;
let _recentStutters = 0;
let _recentSevere = 0;
let _totalStutters = 0;
let _totalSevere = 0;
let _worstFrameTime = 0;

// GC pause estimation (frame time spikes >> average)
let _estimatedGcPauses = 0;

// Network message accumulator (reset each frame)
let _netTimeThisFrame = 0;
let _netCountThisFrame = 0;

// Signal for UI reactivity (updated sparingly, ~2x/sec)
export const frameProfileVersion = signal(0);

// ============================================================================
// Public API - Hot Path (called every frame, must be zero-allocation)
// ============================================================================

export const setProfilerEnabled = ({ enabled }: { enabled: boolean }): void => {
  _enabled = enabled;
  if (!enabled) {
    for (const buf of _sectionBuffers.values()) {
      resetBuffer({ buffer: buf });
    }
    resetBuffer({ buffer: _frameHistory });
    _stutterWriteIdx = 0;
    _stutterHistoryLen = 0;
    _recentStutters = 0;
    _recentSevere = 0;
    _totalStutters = 0;
    _totalSevere = 0;
    _worstFrameTime = 0;
    _estimatedGcPauses = 0;
    _netTimeThisFrame = 0;
    _netCountThisFrame = 0;
  }
};

export const isProfilerEnabled = (): boolean => _enabled;

// Record timing for a named section (render layer, game phase, etc.)
export const recordSectionTime = ({
  name,
  durationMs,
}: {
  name: string;
  durationMs: number;
}): void => {
  if (!_enabled) {
    return;
  }
  const buf = _sectionBuffers.get(name);
  if (buf) {
    pushToBuffer({ buffer: buf, value: durationMs });
  }
};

// Accumulate network message processing time for the current frame
export const recordNetworkMessage = ({ durationMs }: { durationMs: number }): void => {
  if (!_enabled) {
    return;
  }
  _netTimeThisFrame += durationMs;
  _netCountThisFrame++;
};

// Update stutter rolling window counters
const updateStutterTracking = ({ frameTimeMs }: { frameTimeMs: number }): void => {
  const isStutter = frameTimeMs > STUTTER_MS;
  const isSevere = frameTimeMs > SEVERE_STUTTER_MS;

  // Remove oldest entry from rolling window if full
  if (_stutterHistoryLen >= HISTORY_CAPACITY) {
    const oldFlag = _stutterFlags[_stutterWriteIdx] ?? 0;
    if (oldFlag >= 1) {
      _recentStutters--;
    }
    if (oldFlag >= 2) {
      _recentSevere--;
    }
  }

  // Write new entry
  const flag = isSevere ? 2 : isStutter ? 1 : 0;
  _stutterFlags[_stutterWriteIdx] = flag;
  _stutterWriteIdx = (_stutterWriteIdx + 1) % HISTORY_CAPACITY;
  if (_stutterHistoryLen < HISTORY_CAPACITY) {
    _stutterHistoryLen++;
  }

  if (isStutter) {
    _recentStutters++;
    _totalStutters++;
  }
  if (isSevere) {
    _recentSevere++;
    _totalSevere++;
  }

  // Track worst frame
  if (frameTimeMs > _worstFrameTime) {
    _worstFrameTime = frameTimeMs;
  }

  // GC pause estimation: spike > GC_MULTIPLIER * rolling average
  const avg = bufferAvg({ buffer: _frameHistory });
  if (frameTimeMs > avg * GC_MULTIPLIER && frameTimeMs > STUTTER_MS) {
    _estimatedGcPauses++;
  }
};

// Call at end of each frame to record frame time and detect stutters
export const recordFrame = ({ frameTimeMs }: { frameTimeMs: number }): void => {
  if (!_enabled) {
    return;
  }

  // Record frame time to history
  pushToBuffer({ buffer: _frameHistory, value: frameTimeMs });

  // Flush accumulated network time as a section
  const netBuf = _sectionBuffers.get(NETWORK_SECTION);
  if (netBuf) {
    pushToBuffer({ buffer: netBuf, value: _netTimeThisFrame });
  }
  _netTimeThisFrame = 0;
  _netCountThisFrame = 0;

  // Stutter detection
  updateStutterTracking({ frameTimeMs });
};

// ============================================================================
// Public API - UI Reads (called sparingly from debug overlay sync)
// ============================================================================

export interface SectionStats {
  readonly name: string;
  readonly last: number;
  readonly avg: number;
  readonly max: number;
}

export interface ProfileSnapshot {
  readonly phases: ReadonlyArray<SectionStats>;
  readonly layers: ReadonlyArray<SectionStats>;
  readonly network: SectionStats;
  readonly percentiles: {
    readonly p50: number;
    readonly p95: number;
    readonly p99: number;
  };
  readonly stutters: {
    readonly recent: number;
    readonly recentSevere: number;
    readonly total: number;
    readonly worst: number;
    readonly estimatedGcPauses: number;
  };
}

const getSectionStats = ({ name }: { name: string }): SectionStats => {
  const buf = _sectionBuffers.get(name);
  if (!buf || buf.count === 0) {
    return { name, last: 0, avg: 0, max: 0 };
  }
  return {
    name,
    last: Math.round(bufferLast({ buffer: buf }) * 100) / 100,
    avg: Math.round(bufferAvg({ buffer: buf }) * 100) / 100,
    max: Math.round(bufferMax({ buffer: buf }) * 100) / 100,
  };
};

const getPercentile = ({ percentile }: { percentile: number }): number => {
  const count = _frameHistory.count;
  if (count === 0) {
    return 0;
  }

  // Copy valid entries to sort buffer
  for (let i = 0; i < count; i++) {
    _sortBuf[i] = _frameHistory.values[i] ?? 0;
  }

  // Sort in-place (Float64Array.sort is zero-allocation)
  const slice = _sortBuf.subarray(0, count);
  slice.sort();

  const idx = Math.min(Math.floor(count * percentile), count - 1);
  return Math.round((slice[idx] ?? 0) * 10) / 10;
};

// Build a snapshot of all profiler data for the debug overlay.
// Allocates objects - only call this sparingly (e.g., 2x/sec from signal sync).
export const getProfileSnapshot = (): ProfileSnapshot | null => {
  if (!_enabled) {
    return null;
  }

  // Phase stats
  const phases: SectionStats[] = [];
  for (const name of PHASE_NAMES) {
    phases.push(getSectionStats({ name }));
  }

  // Layer stats (sorted by avg descending, filtered to > 0.05ms)
  const layers: SectionStats[] = [];
  for (const name of RENDER_LAYER_NAMES) {
    const stats = getSectionStats({ name });
    if (stats.avg > 0.05 || stats.last > 0.05) {
      layers.push(stats);
    }
  }
  layers.sort((a, b) => b.avg - a.avg);

  return {
    phases,
    layers,
    network: getSectionStats({ name: NETWORK_SECTION }),
    percentiles: {
      p50: getPercentile({ percentile: 0.5 }),
      p95: getPercentile({ percentile: 0.95 }),
      p99: getPercentile({ percentile: 0.99 }),
    },
    stutters: {
      recent: _recentStutters,
      recentSevere: _recentSevere,
      total: _totalStutters,
      worst: Math.round(_worstFrameTime * 10) / 10,
      estimatedGcPauses: _estimatedGcPauses,
    },
  };
};

// Bump version signal to trigger UI re-render (called from syncDebugSignals)
export const syncProfilerSignal = (): void => {
  if (_enabled) {
    frameProfileVersion.value++;
  }
};
